import React, { useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { diffNames, DiffSegment } from "../../lib/name-diff";

/**
 * RevisionConfirmDialog
 *
 * Blocking confirmation shown on the target step when importing as a REVISION: a revision
 * overwrites the selected target process, which is hard to undo, so the user must verify the
 * source ⇄ target pairing before proceeding. Shows both names as a two-line code-style diff
 * (character differences highlighted) and gates Confirm behind an acknowledgement checkbox.
 */
interface RevisionConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceName: string;
  sourceCode?: string | null;
  targetName: string;
  targetCode?: string | null;
  /** Called only after the user acknowledged and clicked Confirm. */
  onConfirm: () => void;
}

const segmentClass = (type: DiffSegment["type"]): string => {
  switch (type) {
    case "removed":
      return "bg-red-500/20 text-red-700 dark:text-red-400 line-through decoration-red-700/60";
    case "added":
      return "bg-emerald-500/20 text-emerald-700 dark:text-emerald-400";
    default:
      return "";
  }
};

const DiffLine: React.FC<{
  sign: "-" | "+";
  label: string;
  code?: string | null;
  segments: DiffSegment[];
  show: DiffSegment["type"];
}> = ({ sign, label, code, segments, show }) => (
  <div
    className={
      "flex items-start gap-2 px-3 py-2 font-mono text-sm " +
      (sign === "-" ? "bg-red-500/5" : "bg-emerald-500/5")
    }
  >
    <span className={sign === "-" ? "text-red-600 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400"}>
      {sign}
    </span>
    <div className="min-w-0 flex-1">
      <div className="break-words">
        {segments
          .filter((s) => s.type === "same" || s.type === show)
          .map((s, i) => (
            <span key={i} className={segmentClass(s.type === "same" ? "same" : s.type)}>
              {s.text}
            </span>
          ))}
      </div>
      <div className="mt-0.5 text-[11px] leading-none text-muted-foreground font-sans">
        {label}
        {code ? ` · ${code}` : ""}
      </div>
    </div>
  </div>
);

export function RevisionConfirmDialog({
  open,
  onOpenChange,
  sourceName,
  sourceCode,
  targetName,
  targetCode,
  onConfirm,
}: RevisionConfirmDialogProps) {
  const [acknowledged, setAcknowledged] = useState(false);

  // Fresh acknowledgement every time the dialog opens.
  useEffect(() => {
    if (open) setAcknowledged(false);
  }, [open]);

  const segments = diffNames(sourceName, targetName);
  const namesMatch = sourceName === targetName;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Confirm revision target</DialogTitle>
          <DialogDescription>
            The revision overwrites the selected target process — verify the names below match
            what you intend before continuing.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border divide-y overflow-hidden">
          <DiffLine sign="-" label="Source process" code={sourceCode} segments={segments} show="removed" />
          <DiffLine sign="+" label="Target process (will be revised)" code={targetCode} segments={segments} show="added" />
        </div>

        {namesMatch ? (
          <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            <span>The names match.</span>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            The names differ — highlighted characters exist on only one side. Make sure this is
            the process you intend to revise.
          </p>
        )}

        <div className="flex items-start gap-2">
          <Checkbox
            id="revision-ack"
            checked={acknowledged}
            onCheckedChange={(v) => setAcknowledged(v === true)}
            className="mt-0.5"
          />
          <Label htmlFor="revision-ack" className="cursor-pointer font-normal leading-snug">
            I've verified this is the correct process to revise.
          </Label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!acknowledged} onClick={onConfirm}>
            Confirm &amp; continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
